import { AtSign, CalendarDays, ChevronDown, ChevronRight, ExternalLink, MapPin, Phone, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { copy } from '../../shared/i18n';

export function LandingPage() {
  const [openService, setOpenService] = useState<string | null>(copy.landing.serviceCards[0]?.title ?? null);
  return (
    <main className="min-h-screen bg-[#f8faf6] text-[#223126]">
      <header className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-6 lg:px-8">
        <a href="/" className="flex min-w-0 items-center gap-3">
          <img src="/happy-bark-logo.webp" alt="Happy Bark" className="h-12 w-12 rounded-full object-cover ring-1 ring-[#d9dfd1]" />
          <div className="min-w-0">
            <p className="text-sm font-semibold uppercase text-[#7a876f]">Happy Bark</p>
            <p className="truncate text-xs text-[#66705f]">{copy.landing.subtitle}</p>
          </div>
        </a>
        <nav className="flex items-center gap-2">
          <a className="hidden rounded-md px-3 py-2 text-sm font-medium text-[#506047] transition hover:bg-white hover:text-[#263426] sm:inline-flex" href="#services">
            {copy.landing.services}
          </a>
          <a className="hidden rounded-md px-3 py-2 text-sm font-medium text-[#506047] transition hover:bg-white hover:text-[#263426] sm:inline-flex" href="#visit">
            {copy.landing.visit}
          </a>
          <Link className="hidden rounded-md px-3 py-2 text-sm font-medium text-[#506047] transition hover:bg-white hover:text-[#263426] sm:inline-flex" to="/photos">
            Fotos
          </Link>
          <Link className="inline-flex h-9 items-center justify-center rounded-md border border-[#cfd8c6] bg-white px-3 text-sm font-semibold text-[#344332] shadow-sm transition hover:bg-[#eef3ea]" to="/admin">
            {copy.landing.staff}
          </Link>
        </nav>
      </header>

      <section className="mx-auto grid min-h-[calc(100vh-5rem)] max-w-7xl items-center gap-10 px-5 pb-14 pt-5 sm:px-6 lg:grid-cols-[1.02fr_0.98fr] lg:px-8">
        <div className="max-w-2xl">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#d7ded0] bg-white px-3 py-1 text-xs font-semibold uppercase text-[#7d4f43] shadow-sm">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            {copy.landing.eyebrow}
          </div>
          <h1 className="max-w-3xl text-5xl font-semibold leading-[1.02] text-[#1f2d22] sm:text-6xl lg:text-7xl">
            <span className="block">Happy Bark</span><span className="ml-8 block sm:ml-14">Happy Life</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-7 text-[#5b6656] sm:text-lg">
            {copy.landing.body}
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <a className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#75816a] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#65735c]" href="#contact">
              {copy.landing.planVisit}
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <Link className="inline-flex h-11 items-center justify-center rounded-md border border-[#d2dacb] bg-white px-5 text-sm font-semibold text-[#2f3d2e] shadow-sm transition hover:bg-[#eef3ea]" to="/photos">
              Ver fotos
            </Link>
          </div>
        </div>

        <div className="mx-auto w-full max-w-xl">
          <div className="relative mx-auto aspect-square max-w-lg overflow-hidden rounded-full border border-[#dce3d5] bg-white shadow-2xl shadow-[#536049]/15">
            <img src="/happy-bark-logo.webp" alt={copy.landing.heroAlt} className="h-full w-full rounded-full object-cover" />
          </div>
          <div className="relative z-10 mx-5 -mt-5 rounded-md border border-[#d7ded0] bg-white/95 p-2 shadow-lg backdrop-blur">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md bg-[#f8faf6] px-3 py-2">
                <p className="text-xs text-[#6a7464]">{copy.landing.tiles.club.label}</p>
                <p className="text-sm font-semibold text-[#263426]">{copy.landing.tiles.club.value}</p>
              </div>
              <div className="rounded-md bg-[#f8faf6] px-3 py-2">
                <p className="text-xs text-[#6a7464]">{copy.landing.tiles.hotel.label}</p>
                <p className="text-sm font-semibold text-[#263426]">{copy.landing.tiles.hotel.value}</p>
              </div>
              <div className="rounded-md bg-[#f8faf6] px-3 py-2">
                <p className="text-xs text-[#6a7464]">{copy.landing.tiles.spa.label}</p>
                <p className="text-sm font-semibold text-[#263426]">{copy.landing.tiles.spa.value}</p>
              </div>
            </div>
            <a
              className="mt-2 flex items-center gap-3 rounded-md border border-[#dfe5d8] bg-[#fbfcfa] p-3 text-left transition hover:border-[#cbd6c2] hover:bg-[#eef3ea]"
              href={copy.landing.locationUrl}
              target="_blank"
              rel="noreferrer"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#eef3ea] text-[#75816a]">
                <MapPin className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-[#263426]">{copy.landing.locationTitle}</span>
                <span className="block text-xs text-[#6b7566]">{copy.landing.locationBody}</span>
              </span>
              <ExternalLink className="h-4 w-4 shrink-0 text-[#7a876f]" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      <section id="services" className="border-y border-[#dfe5d8] bg-white py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="text-sm font-semibold uppercase text-[#cf7766]">{copy.landing.careMenu}</p>
              <h2 className="mt-2 text-3xl font-semibold text-[#223126]">{copy.landing.servicesHeading}</h2>
            </div>
            <p className="max-w-lg text-sm leading-6 text-[#63705f]">{copy.landing.servicesBody}</p>
          </div>
          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            {copy.landing.serviceCards.map((service) => (
              <article key={service.title} className="min-w-0 rounded-lg border border-[#dfe5d8] bg-[#fbfcfa] shadow-sm">
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-5 py-4 text-left"
                  aria-expanded={openService === service.title}
                  onClick={() => setOpenService((current) => current === service.title ? null : service.title)}
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f3e5df] text-sm font-semibold text-[#b76152]">
                    {service.title.slice(0, 1)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xl font-semibold text-[#263426]">{service.title}</span>
                    <span className="mt-1 block truncate text-sm text-[#63705f]">{service.body}</span>
                  </span>
                  <ChevronDown className={`h-4 w-4 shrink-0 text-[#7a876f] transition ${openService === service.title ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
                {openService === service.title ? <p className="border-t border-[#e4eadf] px-5 py-4 text-[0.95rem] leading-7 text-[#5d6959]">{service.body}</p> : null}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="visit" className="bg-[#f8faf6] py-16">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 sm:px-6 lg:grid-cols-[1fr_0.82fr] lg:px-8">
          <div>
            <p className="text-sm font-semibold uppercase text-[#7a876f]">{copy.landing.startHere}</p>
            <h2 className="mt-2 text-3xl font-semibold text-[#223126]">{copy.landing.firstVisitHeading}</h2>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-[#63705f]">
              {copy.landing.firstVisitBody}
            </p>
          </div>
          <div className="grid gap-3 rounded-lg border border-[#dfe5d8] bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#eef3ea] text-[#75816a]">
                <CalendarDays className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold text-[#263426]">{copy.landing.reservationsTitle}</p>
                <p className="text-xs text-[#6b7566]">{copy.landing.reservationsBody}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f3e5df] text-[#cf7766]">
                <MapPin className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold text-[#263426]">{copy.landing.placeTitle}</p>
                <p className="text-xs text-[#6b7566]">{copy.landing.placeBody}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="contact" className="scroll-mt-4 border-t border-[#455443] bg-[#223126] py-16 text-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 sm:px-6 lg:grid-cols-[1fr_0.9fr] lg:items-center lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase text-[#e2a292]">Contacto</p>
            <h2 className="mt-2 text-3xl font-semibold sm:text-4xl">Planea una visita</h2>
            <p className="mt-3 max-w-xl text-sm leading-6 text-[#d9e0d5] sm:text-base">
              Habla directamente con Happy Bark para conocer el club, resolver tus dudas y preparar la primera visita de tu perro.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            <a className="flex min-h-20 items-center gap-4 rounded-md border border-[#60705d] bg-white px-5 py-4 text-[#223126] transition hover:bg-[#eef3ea]" href="tel:+5214495373589">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#f3e5df] text-[#b76152]"><Phone className="h-5 w-5" aria-hidden="true" /></span>
              <span><span className="block text-xs font-semibold uppercase text-[#687461]">Telefono</span><span className="mt-1 block font-semibold">+52 1 449 537 3589</span></span>
            </a>
            <a className="flex min-h-20 items-center gap-4 rounded-md border border-[#60705d] bg-white px-5 py-4 text-[#223126] transition hover:bg-[#eef3ea]" href="https://www.instagram.com/happybarkmx" target="_blank" rel="noreferrer">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#eef3ea] text-[#52634d]"><AtSign className="h-5 w-5" aria-hidden="true" /></span>
              <span className="min-w-0 flex-1"><span className="block text-xs font-semibold uppercase text-[#687461]">Instagram</span><span className="mt-1 block font-semibold">@happybarkmx</span></span>
              <ExternalLink className="h-4 w-4 shrink-0 text-[#687461]" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
